/** Task tag utilities — validation, denormalization, suggestions. */

export const TAG_COLOR_TOKENS = [
  'blue', 'green', 'amber', 'red', 'purple', 'teal', 'slate', 'rose',
];

export const DEFAULT_TAG_COLOR = 'slate';

export const MAX_TAG_NAME_LENGTH = 40;
export const MAX_TAGS_PER_TASK = 8;

export const DEFAULT_TASK_TAGS = [
  { name: 'Rush Order', color: 'red', sortOrder: 1 },
  { name: 'High Priority', color: 'amber', sortOrder: 2 },
  { name: 'Training', color: 'blue', sortOrder: 3 },
  { name: 'Customer Issue', color: 'rose', sortOrder: 4 },
  { name: 'Rework', color: 'purple', sortOrder: 5 },
  { name: 'Material Delay', color: 'amber', sortOrder: 6 },
  { name: 'QA Review', color: 'teal', sortOrder: 7 },
  { name: 'Special Request', color: 'green', sortOrder: 8 },
  { name: 'New Product', color: 'blue', sortOrder: 9 },
  { name: 'Repeat Job', color: 'slate', sortOrder: 10 },
];

const SUGGESTION_RULES = [
  { patterns: [/\brush\b/i, /urgent/i, /asap/i], tagNames: ['Rush Order', 'High Priority'] },
  { patterns: [/\btrain(ing|ee)?\b/i, /new hire/i, /onboarding/i], tagNames: ['Training'] },
  { patterns: [/\bcustomer\b/i, /complaint/i, /client issue/i], tagNames: ['Customer Issue'] },
  { patterns: [/\brework\b/i, /redo\b/i, /fix.?up/i], tagNames: ['Rework'] },
  { patterns: [/\bmaterial\b/i, /supply/i, /waiting for/i, /shortage/i], tagNames: ['Material Delay'] },
  { patterns: [/\bqa\b/i, /quality review/i, /hold\b/i], tagNames: ['QA Review'] },
  { patterns: [/\bspecial\b/i, /one.?off/i, /custom order/i], tagNames: ['Special Request'] },
  { patterns: [/\bnew product\b/i, /first run/i, /launch/i], tagNames: ['New Product'] },
  { patterns: [/\brepeat\b/i, /reorder/i, /same as last/i], tagNames: ['Repeat Job'] },
  { patterns: [/\bhigh.?priority\b/i, /priority/i], tagNames: ['High Priority'] },
];

export function normalizeTagName(name) {
  return (name || '').trim().replace(/\s+/g, ' ');
}

export function validateTagName(name, existingNames = []) {
  const normalized = normalizeTagName(name);
  if (!normalized) return 'required';
  if (normalized.length > MAX_TAG_NAME_LENGTH) return 'tooLong';
  const lower = normalized.toLowerCase();
  if (existingNames.some((n) => n.toLowerCase() === lower)) return 'duplicate';
  return null;
}

/** Build denormalized tag fields for a task document. */
export function resolveTaskTagFields(tagIds, tagsById) {
  const ids = Array.isArray(tagIds) ? tagIds.filter(Boolean).slice(0, MAX_TAGS_PER_TASK) : [];
  const names = ids
    .map((id) => tagsById.get(id)?.name)
    .filter(Boolean);
  return { tagIds: ids, tagNames: names };
}

/** Merge stored tag names with live tag data for display (archived tags keep historical names). */
export function getTaskTagDisplayList(task, tagsById = new Map()) {
  const ids = task.tagIds || [];
  const storedNames = task.tagNames || [];
  return ids.map((id, i) => {
    const live = tagsById.get(id);
    return {
      id,
      name: live?.name || storedNames[i] || storedNames.find((_, idx) => ids[idx] === id) || id,
      color: live?.color || DEFAULT_TAG_COLOR,
      archived: live ? !live.isActive : true,
    };
  });
}

export function countTagUsage(tasks, tagId) {
  if (!tagId || !Array.isArray(tasks)) return 0;
  return tasks.filter((t) => t.tagIds?.includes(tagId)).length;
}

export function suggestTagsFromText(text, availableTags = []) {
  if (!text?.trim() || !availableTags.length) return [];
  const active = availableTags.filter((t) => t.isActive !== false);
  const byName = new Map(active.map((t) => [t.name.toLowerCase(), t]));
  const suggested = new Set();

  for (const rule of SUGGESTION_RULES) {
    if (rule.patterns.some((p) => p.test(text))) {
      for (const name of rule.tagNames) {
        const tag = byName.get(name.toLowerCase());
        if (tag) suggested.add(tag.id);
      }
    }
  }

  return [...suggested].map((id) => active.find((t) => t.id === id)).filter(Boolean);
}

const RECENT_KEY = (companyId) => `floora_recent_tags_${companyId}`;

export function getRecentTagIds(companyId) {
  if (!companyId) return [];
  try {
    const raw = localStorage.getItem(RECENT_KEY(companyId));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.slice(0, 6) : [];
  } catch {
    return [];
  }
}

export function recordRecentTagIds(companyId, tagIds) {
  if (!companyId || !tagIds?.length) return;
  try {
    const prev = getRecentTagIds(companyId);
    const merged = [...tagIds, ...prev.filter((id) => !tagIds.includes(id))].slice(0, 8);
    localStorage.setItem(RECENT_KEY(companyId), JSON.stringify(merged));
  } catch {
    // ignore storage errors
  }
}

export function sortTagsForPicker(tags, { recentIds = [], usageCounts = {} } = {}) {
  return [...tags].sort((a, b) => {
    const aRecent = recentIds.indexOf(a.id);
    const bRecent = recentIds.indexOf(b.id);
    if (aRecent !== -1 || bRecent !== -1) {
      if (aRecent === -1) return 1;
      if (bRecent === -1) return -1;
      return aRecent - bRecent;
    }
    const aUse = usageCounts[a.id] || 0;
    const bUse = usageCounts[b.id] || 0;
    if (aUse !== bUse) return bUse - aUse;
    return (a.sortOrder || 0) - (b.sortOrder || 0) || a.name.localeCompare(b.name);
  });
}
